import styled from "@emotion/styled";
import {FC} from "react";
import {Controller, ControllerFieldState, ControllerRenderProps, useWatch} from "react-hook-form";
import {ETANotificationConfigsFormValues, useETANotificationContext} from "../ETANotificationConfigsProvider";
import {Button, Menu, MenuItem, Popover} from "@blueprintjs/core";
import {ETAInput} from "../common/ETANotificationConfigs.style";
import {ETAInputWrapperWithErrorMessage} from "./ETAInputWrapperWithErrorMessage";
import {
  handleNumberChange,
  handleNumberKeyDown,
  handleNumberPaste,
  MAX_TRIGGER_INPUT_DIGITS
} from "../common/ETANotificationConfigs.utils";

export interface ETATriggerSettingsProps {
  fieldSuffix: string;
  disabled?: boolean;
  triggerWhenText?: string;
  // Optional cross-field rule applied to the `awayFrom` input only. Returns `true`
  // when valid or an error message string. Supplied by the parent so this component
  // stays generic (used for 1st / 2nd / optimization notifications). See DP-3044.
  crossFieldValidate?: (value: unknown) => true | string;
}

export const ETATriggerSettings: FC<ETATriggerSettingsProps> = ({
  fieldSuffix,
  disabled,
  triggerWhenText,
  crossFieldValidate
}) => {
  const {
    etaFormState: {control}
  } = useETANotificationContext();

  const triggerWhen = useWatch({
    control,
    name: `${fieldSuffix}.triggerWhen` as any
  });

  const validationRules = {
    required: {
      value: !disabled,
      message: "This field is required"
    },
    validate: (value: any) => {
      if (disabled) return true;
      if (value === null || value === undefined) return false;
      const numValue = typeof value === "string" ? Number.parseFloat(value) : value;
      return !Number.isNaN(numValue) && value !== "";
    }
  };

  // The `awayFrom` field runs the base numeric/required check first, then the
  // optional cross-field rule. The base check returning false (NaN/empty) takes
  // precedence so the cross-field comparison only runs on valid numeric input.
  const awayFromValidationRules = {
    required: validationRules.required,
    validate: (value: any) => {
      const baseResult = validationRules.validate(value);
      if (baseResult !== true) return baseResult;
      if (disabled) return true;
      if (crossFieldValidate) return crossFieldValidate(value);
      return true;
    }
  };

  const renderETATriggerAwayFrom = ({
    field,
    fieldState
  }: {
    field: ControllerRenderProps<ETANotificationConfigsFormValues, any>;
    fieldState: ControllerFieldState;
  }) => {
    const {value, onChange, disabled} = field;

    return (
      <ETAInputWrapperWithErrorMessage errorMessage={fieldState.error?.message}>
        <div style={{position: "relative"}}>
          <ETAInput
            value={value}
            onChange={handleNumberChange(onChange)}
            onKeyDown={handleNumberKeyDown}
            onPaste={handleNumberPaste(onChange, MAX_TRIGGER_INPUT_DIGITS)}
            width={"113px"}
            type={"number"}
            padding={"8px 50px 8px 8px"}
            disabled={disabled}
            data-testid={`${fieldSuffix}-away-from-input`}
          />
          <div style={{position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)"}}>
            {triggerWhen === "eta" ? "min" : "stops"}
          </div>
        </div>
      </ETAInputWrapperWithErrorMessage>
    );
  };

  return (
    <ETATriggerSettingsContainer className={disabled ? "premium-disabled-block" : ""}>
      {triggerWhenText ? (
        <ETATriggerSettingsRow>{triggerWhenText}</ETATriggerSettingsRow>
      ) : (
        <ETATriggerSettingsRow>
          <ETATriggerSettingsLabel>Trigger when</ETATriggerSettingsLabel>
          <ETATriggerSettingsBackground className="input-background">
            <Controller
              name={`${fieldSuffix}.triggerWhen` as any}
              control={control}
              render={renderTriggerWhen}
              disabled={disabled}
            />
          </ETATriggerSettingsBackground>
          <div>is</div>
          <ETATriggerSettingsBackground className="input-background">
            <Controller
              name={`${fieldSuffix}.awayFrom` as any}
              control={control}
              rules={awayFromValidationRules}
              disabled={disabled}
              render={renderETATriggerAwayFrom}
            />
            <div>{`away from the current ${triggerWhen === "eta" ? "time" : "stop"}`}</div>
          </ETATriggerSettingsBackground>
        </ETATriggerSettingsRow>
      )}
      <ETATriggerSettingsRow>
        <ETATriggerSettingsLabel>Time Window:</ETATriggerSettingsLabel>
        <ETATriggerSettingsBackground className="input-background">
          <Controller
            name={`${fieldSuffix}.timeWindowBeforeETA` as any}
            control={control}
            rules={validationRules}
            render={renderETATimeWindowBefore}
            disabled={disabled}
          />
          <div>before the ETA</div>
        </ETATriggerSettingsBackground>
        <div>to</div>
        <ETATriggerSettingsBackground className="input-background">
          <Controller
            name={`${fieldSuffix}.timeWindowAfterETA` as any}
            control={control}
            rules={validationRules}
            render={renderETATimeWindowAfter}
            disabled={disabled}
          />
          <div>after the ETA</div>
        </ETATriggerSettingsBackground>
      </ETATriggerSettingsRow>
    </ETATriggerSettingsContainer>
  );
};

const renderTriggerWhen = ({field}: {field: ControllerRenderProps<ETANotificationConfigsFormValues, any>}) => {
  const {value, onChange, disabled} = field;
  const handleChangeValue = (e: any) => {
    onChange(e.target.innerText.toLowerCase());
  };

  return (
    <Popover
      placement={"bottom"}
      content={
        <Menu>
          <MenuItem text={"ETA"} active={value === "eta"} onClick={handleChangeValue} disabled={disabled} />
          <MenuItem text={"Stop"} active={value === "stop"} onClick={handleChangeValue} disabled={disabled} />
        </Menu>
      }
      matchTargetWidth={true}
    >
      <ETATriggerSettingsButton endIcon={"caret-down"} disabled={disabled}>
        {value === "eta" ? "ETA" : "Stop"}
      </ETATriggerSettingsButton>
    </Popover>
  );
};

const renderETATimeWindowAfter = ({
  field,
  fieldState
}: {
  field: ControllerRenderProps<ETANotificationConfigsFormValues, any>;
  fieldState: ControllerFieldState;
}) => {
  const {value, onChange, disabled} = field;

  return (
    <ETAInputWrapperWithErrorMessage errorMessage={fieldState.error?.message}>
      <div style={{position: "relative"}}>
        <ETAInput
          value={value}
          onChange={handleNumberChange(onChange)}
          onKeyDown={handleNumberKeyDown}
          onPaste={handleNumberPaste(onChange, MAX_TRIGGER_INPUT_DIGITS)}
          width={"113px"}
          type={"number"}
          padding={"8px 50px 8px 8px"}
          disabled={disabled}
          data-testid="eta-time-window-after-input"
        />
        <div style={{position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)"}}>min</div>
      </div>
    </ETAInputWrapperWithErrorMessage>
  );
};

const renderETATimeWindowBefore = ({
  field,
  fieldState
}: {
  field: ControllerRenderProps<ETANotificationConfigsFormValues, any>;
  fieldState: ControllerFieldState;
}) => {
  const {value, onChange, disabled} = field;

  return (
    <ETAInputWrapperWithErrorMessage errorMessage={fieldState.error?.message}>
      <div style={{position: "relative"}}>
        <ETAInput
          value={value}
          onChange={handleNumberChange(onChange)}
          onKeyDown={handleNumberKeyDown}
          onPaste={handleNumberPaste(onChange, MAX_TRIGGER_INPUT_DIGITS)}
          width={"113px"}
          type={"number"}
          padding={"8px 50px 8px 8px"}
          disabled={disabled}
          data-testid="eta-time-window-before-input"
        />
        <div style={{position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)"}}>min</div>
      </div>
    </ETAInputWrapperWithErrorMessage>
  );
};

export const ETATriggerSettingsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
`;

export const ETATriggerSettingsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: fit-content;
  gap: 16px;
`;

export const ETATriggerSettingsLabel = styled.div`
  width: 96px;
  text-align: left;
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
`;

export const ETATriggerSettingsBackground = styled.div`
  padding: 4px 12px 4px 4px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const ETATriggerSettingsButton = styled(Button)`
  width: 248px;
  border-radius: 8px;
  background-color: #ffffff !important;
  display: flex !important;
  justify-content: space-between !important;
`;
